import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { DataSecuritySettings } from '@/core/models/filing.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { selectedClient } from '@/core/selectedClient';
import { filingService } from '@/core/services/filing';
import { firmSettingsService } from '@/core/services/firmSettings';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { useDirtyForm } from '@/kit/hooks/useDirtyForm';
import { InfoHint } from '@/kit/InfoHint';
import { LeaveGuard } from '@/kit/LeaveGuard';
import { PageHeader, PageHeaderFooter } from '@/kit/PageHeader';
import { Toggle } from '@/kit/Toggle';
import './TaxreturnDatasecurity.scss';

const GDPR_TEXT =
  'As per the GDPR(General Data Protection Regulation), Capium provides with the option to password protect Accounts Report PDF.After the password has been set the document will only open with the password. Please also note that this password is case sensitive';
const MASTER_PASSWORD_TEXT =
  'The Master password helps clients,accountants and staff to view Accounts Report PDF,Please copy and paste this password into the PDF Viewer to unlock it for you';
const ADMIN_ONLY = 'Only a firm admin can change this setting. You can see its current state here.';

interface DataSecurityValues {
  isEnabled: boolean;
  masterPassword: string;
}

/** Angular TaxreturnDatasecurityComponent: password protection of the client's Accounts Report PDF. */
export function TaxreturnDatasecurity() {
  const navigate = useNavigate();
  const [client] = useState(() => selectedClient());
  /**
   * Design review 2 Nov 2026: always editable for an admin. `saved` (BUG-043) is the setting as last loaded or saved - what
   * the PDFs actually follow, unlike the fields being edited - and `dirty` is what the header's Save and Discard follow.
   */
  const form = useDirtyForm<DataSecurityValues>({ isEnabled: false, masterPassword: '' });
  const { isEnabled, masterPassword } = form.values;
  const saved = form.saved;
  const [loaded, setLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const [showPassword, setShowPassword] = useState(false);
  /** True after a save was refused: an empty mandatory master password is outlined red. */
  const [submitted, setSubmitted] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  /**
   * BUG-043: the firm-wide switch (firm Settings > Data Security) can turn every client's protection off. A firm that
   * never set it reads as on (FirmPreferenceService). Null until known - nothing is claimed while it is unknown.
   */
  const [firmEnabled, setFirmEnabled] = useState<boolean | null>(null);

  const setIsEnabled = (on: boolean) => form.setValues((current) => ({ ...current, isEnabled: on }));
  const setMasterPassword = (password: string) =>
    form.setValues((current) => ({ ...current, masterPassword: password }));

  const apply = (settings: DataSecuritySettings) => {
    form.markSaved({ isEnabled: settings.isEnabled, masterPassword: settings.masterPassword ?? '' });
    setLoaded(true);
    setShowPassword(false);
    setCanEdit(settings.canEdit);
  };

  useEffect(() => {
    if (!client) {
      notifyError('Select a client from Accounts first.');
      navigate('/home/accountclient');
      return;
    }
    filingService.getDataSecurity(client.id).then(apply, (error) => notifyError(error));
    firmSettingsService.getDataSecurity().then(
      (firm) => setFirmEnabled(firm.isEnabled),
      () => setFirmEnabled(null),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ngOnInit: once
  }, []);

  const save = () => {
    if (!client || savingRef.current || !canEdit) {
      return;
    }
    const password = masterPassword ?? '';
    setSubmitted(true);
    // The API (DataSecurityService) asks for the master password only when the protection is being turned on.
    if (!password && isEnabled) {
      notifyError('Enter the master password to enable document data security.');
      return;
    }
    if (password.length > 10) {
      notifyError('The master password can be at most 10 characters.');
      return;
    }

    savingRef.current = true;
    setIsSaving(true);
    const finish = () => {
      savingRef.current = false;
      setIsSaving(false);
    };
    filingService.saveDataSecurity(client.id, isEnabled, password || null).then(
      (settings) => {
        finish();
        apply(settings);
        notifySuccess('Password security info saved successfully.');
      },
      (error) => {
        finish();
        notifyError(error);
      },
    );
  };

  const savedOn = loaded && saved.isEnabled && !!saved.masterPassword;
  const localOn = isEnabled && !!masterPassword;
  /** BUG-043: an unsaved change that would turn protection on or off. */
  const pending = loaded && form.dirty && localOn !== savedOn;

  const copyPassword = () => {
    if (!masterPassword) {
      notifyError('No master password is set.');
      return;
    }
    if (!navigator.clipboard) {
      notifyError('Copying to the clipboard is not available in this browser.');
      return;
    }
    navigator.clipboard.writeText(masterPassword).then(
      () => notifySuccess('Master password copied to the clipboard.'),
      () => notifyError('The master password could not be copied.'),
    );
  };

  return (
    <SideMenuPage menu="settings" contentClassName="tr-datasecurity">
      <div className="sidenav-details">
        {/* 4 Nov design review: title hidden (this client's Settings menu already shows "Data Security" highlighted);
            Save and Discard sit in a persistent sticky footer once something changed; the close button really does
            navigate back, so it stays. */}
        <PageHeader
          title="Data Security"
          hideTitle
          actionsPlacement="footer"
          dirty={form.dirty}
          busy={isSaving}
          onSave={save}
          onDiscard={form.discard}
        >
          <InfoHint text={[GDPR_TEXT, MASTER_PASSWORD_TEXT, ...(canEdit ? [] : [ADMIN_ONLY])].join('\n')} />
          <Button className="close-btn" onClick={() => navigate(-1)}>
            <Icon name="close" />
          </Button>
        </PageHeader>
        <LeaveGuard when={form.dirty} />
        <div className="sidenav-body pt-1">
          <div className="mb-10">
            <Toggle checked={isEnabled} disabled={!canEdit} onCheckedChange={setIsEnabled}>
              Enable document Data Security
            </Toggle>
          </div>

          {/* BUG-043: the status reports what is in force - the saved setting under the firm-wide switch - never the
              ticks and password still being edited. */}
          {savedOn && firmEnabled === true && (
            <p className="tr-datasecurity__status">
              <Icon name="lock" outlined className="font-size-16" />
              Protection is active: every Accounts Report PDF generated for this client is encrypted and opens only with
              the master password below.
            </p>
          )}
          {savedOn && firmEnabled === false && (
            <p className="tr-datasecurity__status">
              <Icon name="lock_open" outlined className="font-size-16" />
              Protection is switched off for the whole firm (firm Settings &gt; Data Security): Accounts Report PDFs for
              this client are generated without a password.
            </p>
          )}
          {loaded && !savedOn && (
            <p className="tr-datasecurity__status">
              <Icon name="lock_open" outlined className="font-size-16" />
              Protection is off: Accounts Report PDFs for this client are generated without a password.
            </p>
          )}
          {pending && (
            <p className="tr-datasecurity__status">
              <Icon name="info" outlined className="font-size-16" />
              {isEnabled && masterPassword
                ? 'Protection will apply once saved.'
                : 'Protection will be turned off once saved.'}
            </p>
          )}

          <div className="content-header d-block mt-1">
            <div className="form-field content-header-right d-block">
              <p>
                Master Password{isEnabled && <span className="text-red">*</span>}
              </p>
              <div className="d-flex gap-10">
                <InputField
                  fieldClassName="w-300"
                  invalid={submitted && isEnabled && !masterPassword}
                  maxLength={10}
                  type={showPassword ? 'text' : 'password'}
                  value={masterPassword}
                  disabled={!canEdit}
                  onChange={(event) => setMasterPassword(event.target.value)}
                  suffix={
                    <Icon
                      name={showPassword ? 'visibility_off' : 'visibility'}
                      outlined
                      className="cursor-pointer"
                      onClick={() => setShowPassword((shown) => !shown)}
                    />
                  }
                />
                <Button className="light-bg" title="Copy" aria-label="Copy" onClick={copyPassword}>
                  <Icon name="content_copy" />
                </Button>
              </div>
            </div>
          </div>
        </div>
        <PageHeaderFooter dirty={form.dirty} busy={isSaving} onSave={save} onDiscard={form.discard} />
      </div>
    </SideMenuPage>
  );
}
